import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type ClienteLocal } from '../datos/db.js'
import { Boton } from '../comunes/Boton.js'
import { edadAniosMeses, esMenorDeEdad } from '@practiclinic/core'

/**
 * Ficha del paciente — el detalle que antes vivía en `Fichas.tsx`.
 *
 * Dos canales, y la separación es la que hace que la app arranque en una
 * conexión móvil (§5.5):
 *
 *   básico     nombre, documento, teléfono, cobertura. Viene con toda la
 *              agenda en el canal operativo, así que la búsqueda funciona sin
 *              conexión y es instantánea: se filtra sobre Dexie.
 *   extendido  `datos jsonb`, historial completo. Se pide por paciente al
 *              abrir la ficha. Un odontograma con adjuntos en cada pull
 *              volvería inusable el arranque.
 *
 * **Cambio T22:** la cabecera con el nombre y el recuadro de deuda se mudaron
 * al `BannerPaciente`, que es visible en todas las solapas. Acá queda el
 * contenido: datos básicos, ficha clínica editable y las acciones.
 *
 * **Cambio T23:** el historial se fue a la solapa `Citas` del paciente. Es la
 * misma información —y ahora con las citas futuras que acá no se veían—, así
 * que el `fetch` a `/ficha` sigue sólo para la ficha clínica y los datos
 * extendidos. La pastilla de estado queda exportada para que la use `Citas`.
 */

const API = '/api'

interface FichaExtendida {
  cliente: {
    id: string
    nombre: string
    doc: string | null
    ruc: string | null
    telefono: string | null
    coberturaId: string | null
    waOptin: boolean
    fechaNacimiento: string | null
    datos: Record<string, unknown>
  }
  responsables: {
    id: string; nombre: string; doc: string | null; telefono: string | null
    email: unknown; fechaNacimiento: string | null; parentesco: string
    principal: boolean; autorizaTratamientoRx: boolean
  }[]
}


export function Ficha({
  clienteId,
  onCobrar,
  onAgendar,
  onAnamnesis,
}: {
  clienteId: string
  onCobrar?: ((citaId: string) => void) | undefined
  onAgendar?: ((clienteId: string) => void) | undefined
  onAnamnesis?: (() => void) | undefined
}) {
  const [ficha, setFicha] = useState<FichaExtendida | null>(null)
  const [revision, setRevision] = useState(0)

  // Lo básico sale de Dexie al instante; lo extendido llega después. Así la
  // ficha se pinta enseguida aunque la red esté lenta o no haya.
  const basico = useLiveQuery(() => db.cliente.get(clienteId), [clienteId])

  const cargos = useLiveQuery(
    async () => {
      const citas = await db.cita.where('cliente_id').equals(clienteId).toArray()
      const ids = citas.map((c) => c.id)
      const suyos = await db.cargo.filter((g) => ids.includes(g.cita_id)).toArray()
      const pagos = await db.pago.toArray()
      return suyos
        .map((g) => ({
          ...g,
          pagado: pagos.filter((p) => p.cargo_id === g.id).reduce((a, p) => a + p.monto, 0),
        }))
        // Por id, que es UUIDv7: ordena por antigüedad sin traer otra columna.
        .sort((a, b) => a.id.localeCompare(b.id))
    },
    [clienteId],
    [],
  )

  useEffect(() => {
    let vivo = true
    setFicha(null)
    void fetch(`${API}/clientes/${clienteId}/ficha`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error('No pudimos traer la ficha completa')
        return r.json()
      })
      .then((f) => {
        if (vivo) setFicha(f as FichaExtendida)
      })
      .catch(() => {
        // La ficha clínica es un plus sobre lo que ya está en el dispositivo:
        // si no llega, la ficha se muestra igual con sus datos básicos.
      })
    return () => {
      vivo = false
    }
  }, [clienteId, revision])

  if (!basico) return <p className="p-10 text-center text-slate-400">Paciente no encontrado.</p>

  // La cita del cargo impago más viejo: es la que hay que abrir para cobrar.
  const citaAdeudada = cargos.find((g) => g.monto - g.pagado > 0)?.cita_id ?? null

  return (
    // Sin ancho ni padding propios: los pone el shell, y encimarle otro
    // contenedor es como el contenido terminaba corrido y angosto (AppShell).
    <div className="max-w-2xl">
      {/* Las dos acciones del paciente, en una fila.
          El nombre y el saldo los muestra el banner, así que acá no se repiten:
          quedan sólo las cosas que se pueden hacer. «Ir a cobrar» aparece
          únicamente si hay una cita con saldo, y abre esa cita —antes esto era
          un texto que le pedía a la recepcionista adivinar el día y buscarla a
          mano. */}
      {(onAgendar || (onCobrar && citaAdeudada)) && (
        <div className="mb-5 flex flex-wrap justify-end gap-2">
          {onCobrar && citaAdeudada && (
            <Boton variante="primary" onClick={() => onCobrar(citaAdeudada)}>
              Ir a cobrar
            </Boton>
          )}
          {onAgendar && (
            <Boton variante="secondary" onClick={() => onAgendar(clienteId)}>
              Agendar cita
            </Boton>
          )}
        </div>
      )}

      {ficha && <>
        {(() => {
          const menor = esMenorDeEdad(ficha.cliente.fechaNacimiento, new Date())
          const datosPacienteCompletos = Boolean(
            basico.doc && basico.telefono && ficha.cliente.fechaNacimiento
            && ficha.cliente.datos['apellido'],
          )
          const responsableCompleto = !menor || ficha.responsables.some((responsable) => responsable.principal)
          return <>
            {menor && <div className="mt-5 rounded-xl border-2 border-rose-500 bg-rose-50 p-4 text-center font-bold text-rose-900">PACIENTE MENOR DE EDAD — REQUIERE ACOMPAÑANTE</div>}
            <FichaClinica clienteId={clienteId} cliente={basico} ficha={ficha}
              incompleta={!datosPacienteCompletos} onActualizada={() => setRevision((r) => r + 1)} />
            {menor && <ResponsableMenor clienteId={clienteId} ficha={ficha}
              onActualizada={() => setRevision((r) => r + 1)} />}
            <section className={`mt-5 rounded-xl border p-4 ${datosPacienteCompletos && responsableCompleto ? 'border-slate-200 bg-white' : 'border-amber-300 bg-amber-50'}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium text-slate-800">Anamnesis médica</h3>
                  {(!datosPacienteCompletos || !responsableCompleto) && <p className="mt-1 text-sm text-amber-900">
                    Hay datos pendientes en la ficha{menor && !responsableCompleto ? ' y falta asignar un responsable' : ''}.
                  </p>}
                </div>
                <button type="button" onClick={onAnamnesis} className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white">
                  Ir a anamnesis médica
                </button>
              </div>
            </section>
          </>
        })()}
      </>}
    </div>
  )
}

function ResponsableMenor({ clienteId, ficha, onActualizada }: {
  clienteId: string; ficha: FichaExtendida
  onActualizada: () => void
}) {
  const [responsable, setResponsable] = useState({
    nombreCompleto: '', doc: '', telefono: '', email: '', fechaNacimiento: '', parentesco: 'madre',
    autorizaTratamientoRx: false,
  })
  const [modoResponsable, setModoResponsable] = useState<'existente' | 'reducida'>('existente')
  const [responsableExistenteId, setResponsableExistenteId] = useState('')
  const [mensaje, setMensaje] = useState<string | null>(null)
  const pacientes = useLiveQuery(
    () => db.cliente.filter((cliente) => cliente.id !== clienteId && !cliente.deleted_at).toArray(),
    [clienteId],
    [],
  )

  async function guardarResponsable() {
    const r = await fetch(`${API}/clientes/${clienteId}/responsables`, {
      method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
      body: JSON.stringify(modoResponsable === 'existente' ? {
        responsableId: responsableExistenteId,
        parentesco: responsable.parentesco,
        autorizaTratamientoRx: responsable.autorizaTratamientoRx,
      } : {
        ...responsable,
        fechaNacimiento: responsable.fechaNacimiento || null,
      }),
    })
    if (!r.ok) { setMensaje('No se pudo guardar el responsable'); return }
    setMensaje('Responsable guardado'); onActualizada()
  }

  return <section className="mt-5 rounded-xl border border-rose-200 bg-white p-4 text-sm">
      <h3 className="font-medium text-rose-800">Responsable del menor</h3>
    {ficha.responsables.map((r) => <div key={r.id} className="mt-3 rounded-lg bg-slate-50 p-3">
      <strong>{r.nombre}</strong> · {r.parentesco.replace('_', ' ')}{r.principal ? ' · principal' : ''}<br />
      {r.doc ?? 'sin CI'} · {r.telefono ?? 'sin teléfono'}<br />
      Fecha de nacimiento: {r.fechaNacimiento ?? 'sin cargar'}<br />
      <span className={r.autorizaTratamientoRx ? 'text-teal-700' : 'font-bold text-rose-700'}>{r.autorizaTratamientoRx ? 'Autoriza tratamiento y RX' : 'Autorización pendiente'}</span>
    </div>)}
    <details className="mt-3"><summary className="cursor-pointer text-teal-700">Agregar o cambiar responsable</summary>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={() => setModoResponsable('existente')} className={`rounded-lg px-3 py-2 text-sm ${modoResponsable === 'existente' ? 'bg-teal-700 text-white' : 'bg-slate-100 text-slate-700'}`}>Elegir paciente</button>
        <button type="button" onClick={() => setModoResponsable('reducida')} className={`rounded-lg px-3 py-2 text-sm ${modoResponsable === 'reducida' ? 'bg-teal-700 text-white' : 'bg-slate-100 text-slate-700'}`}>Ficha reducida</button>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {modoResponsable === 'existente' ? <label className="text-xs text-slate-500 sm:col-span-2">Paciente responsable
          <select value={responsableExistenteId} onChange={(e) => setResponsableExistenteId(e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm">
            <option value="">Seleccionar paciente…</option>
            {pacientes.map((paciente) => <option key={paciente.id} value={paciente.id}>{paciente.nombre}{paciente.doc ? ` · ${paciente.doc}` : ''}</option>)}
          </select>
        </label> : <>
          {(['nombreCompleto', 'doc', 'telefono', 'email'] as const).map((k) => <input key={k} value={responsable[k]} onChange={(e) => setResponsable({ ...responsable, [k]: e.target.value })} placeholder={{ nombreCompleto: 'Nombre completo *', doc: 'CI', telefono: 'Teléfono', email: 'Email' }[k]} className="min-h-10 rounded-lg border border-slate-300 px-3" />)}
          <label className="text-xs text-slate-500">Fecha de nacimiento (opcional)<input type="date" value={responsable.fechaNacimiento} onChange={(e) => setResponsable({ ...responsable, fechaNacimiento: e.target.value })} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3" /></label>
        </>}
        <select value={responsable.parentesco} onChange={(e) => setResponsable({ ...responsable, parentesco: e.target.value })} className="min-h-10 rounded-lg border border-slate-300 px-3"><option value="madre">Madre</option><option value="padre">Padre</option><option value="tutor_legal">Tutor legal</option><option value="abuelo">Abuelo/a</option><option value="otro">Otro</option></select>
        <label className="flex items-center gap-2"><input type="checkbox" checked={responsable.autorizaTratamientoRx} onChange={(e) => setResponsable({ ...responsable, autorizaTratamientoRx: e.target.checked })} /> Autoriza tratamiento y RX</label>
      </div>
      <button type="button" disabled={modoResponsable === 'existente' ? !responsableExistenteId : !responsable.nombreCompleto} onClick={() => void guardarResponsable()} className="mt-3 rounded-lg bg-teal-700 px-4 py-2 text-white disabled:opacity-50">Guardar responsable</button>
    </details>
    {mensaje && <p className="mt-2 text-slate-600">{mensaje}</p>}
  </section>
}

/**
 * La ficha schema-driven de §4.5.
 *
 * Es `jsonb` a propósito: cada pack vertical define sus campos, y agregarle una
 * columna a `cliente` por cada cosa que se le ocurra a una clínica es cómo se
 * llega a una tabla con ochenta columnas nulas.
 */
function FichaClinica({
  clienteId,
  cliente,
  ficha,
  incompleta,
  onActualizada,
}: {
  clienteId: string
  cliente: ClienteLocal
  ficha: FichaExtendida
  incompleta: boolean
  onActualizada: () => void
}) {
  const datos = ficha.cliente.datos
  const [editando, setEditando] = useState(incompleta)
  const [nombre, setNombre] = useState(cliente.nombre)
  const [doc, setDoc] = useState(cliente.doc ?? '')
  const [telefono, setTelefono] = useState(cliente.telefono ?? '')
  const [fechaNacimiento, setFechaNacimiento] = useState(ficha.cliente.fechaNacimiento ?? '')
  const [campos, setCampos] = useState(() => ({
    apellido: String(datos.apellido ?? ''),
    tipoDocumento: String(datos.tipoDocumento ?? 'CI'),
    sexo: String(datos.sexo ?? ''),
    nacionalidad: String(datos.nacionalidad ?? ''),
    telefonoSecundario: String(datos.telefonoSecundario ?? ''),
    email: String(datos.email ?? ''),
    direccion: String(datos.direccion ?? ''),
    barrio: String(datos.barrio ?? ''),
    ciudad: String(datos.ciudad ?? ''),
    departamento: String(datos.departamento ?? ''),
    seguroMedico: String(datos.seguroMedico ?? ''),
    numeroContrato: String(datos.numeroContrato ?? ''),
    numeroAfiliado: String(datos.numeroAfiliado ?? ''),
    comoLlego: String(datos.comoLlego ?? ''),
    colegio: String(datos.colegio ?? ''),
  }))
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const edadObj = edadAniosMeses(fechaNacimiento || '', new Date())
  const edad = edadObj ? { anios: edadObj.anios, texto: `${edadObj.anios} años${edadObj.anios < 6 ? ` ${edadObj.meses} meses` : ''}` } : null
  const faltantes = [
    !nombre.trim() && 'nombre', !campos.apellido.trim() && 'apellido', !doc.trim() && 'documento',
    !telefono.trim() && 'teléfono', !fechaNacimiento && 'fecha de nacimiento',
  ].filter(Boolean) as string[]

  async function guardar() {
    setGuardando(true)
    setError(null)
    try {
      const res = await fetch(`${API}/clientes/${clienteId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nombre: nombre.trim(), doc: doc.trim(), telefono: telefono.trim(),
          fechaNacimiento: fechaNacimiento || null,
          datos: campos.seguroMedico ? campos : {
            ...campos, numeroContrato: '', numeroAfiliado: '',
          },
        }),
      })
      if (!res.ok) throw new Error('No pudimos guardar la ficha')
      setEditando(false)
      onActualizada()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo salió mal')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="mt-5">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-medium text-slate-700">Ficha del paciente</h3>
        <button
          type="button"
          onClick={() => setEditando(!editando)}
          className="ml-auto text-sm text-teal-700"
        >
          {editando ? 'Cancelar' : 'Editar'}
        </button>
      </div>

      {error && (
        <p className="mb-2 rounded-lg bg-rose-50 p-2 text-sm text-rose-800">{error}</p>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {faltantes.length > 0 && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm font-medium text-amber-900">Ficha incompleta. Falta completar: {faltantes.join(', ')}.</p>}

        <h4 className="mb-3 text-sm font-semibold text-slate-700">Datos personales</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoFicha etiqueta="Nombre *" valor={nombre} editando={editando} onChange={setNombre} />
          <CampoFicha etiqueta="Apellido *" valor={campos.apellido} editando={editando} onChange={(valor) => setCampos({ ...campos, apellido: valor })} />
          <CampoSelect etiqueta="Tipo de documento" valor={campos.tipoDocumento} editando={editando} opciones={['CI', 'Pasaporte']} onChange={(valor) => setCampos({ ...campos, tipoDocumento: valor })} />
          <CampoFicha etiqueta="N.º de documento *" valor={doc} editando={editando} onChange={setDoc} />
          <div><label className="block text-xs uppercase tracking-wide text-slate-400">Fecha de nacimiento *</label>{editando ? <input type="date" value={fechaNacimiento} onChange={(e) => setFechaNacimiento(e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /> : <p className="text-sm text-slate-800">{fechaNacimiento || '—'}</p>}</div>
          <div><span className="block text-xs uppercase tracking-wide text-slate-400">Edad</span><p className="text-sm text-slate-800">{edad?.texto ?? '—'}</p></div>
          <CampoSelect etiqueta="Sexo" valor={campos.sexo} editando={editando} opciones={['Femenino', 'Masculino', 'Otro', 'Prefiere no indicar']} placeholder="Seleccionar…" onChange={(valor) => setCampos({ ...campos, sexo: valor })} />
          <CampoFicha etiqueta="Nacionalidad" valor={campos.nacionalidad} editando={editando} onChange={(valor) => setCampos({ ...campos, nacionalidad: valor })} />
        </div>

        <h4 className="mb-3 mt-6 border-t border-slate-100 pt-5 text-sm font-semibold text-slate-700">Contacto</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoFicha etiqueta="Teléfono principal *" valor={telefono} editando={editando} onChange={setTelefono} />
          <CampoFicha etiqueta="Teléfono secundario" valor={campos.telefonoSecundario} editando={editando} onChange={(valor) => setCampos({ ...campos, telefonoSecundario: valor })} />
          <CampoFicha etiqueta="Email" valor={campos.email} editando={editando} onChange={(valor) => setCampos({ ...campos, email: valor })} />
          <CampoFicha etiqueta="Calle / dirección" valor={campos.direccion} editando={editando} onChange={(valor) => setCampos({ ...campos, direccion: valor })} />
          <CampoFicha etiqueta="Barrio" valor={campos.barrio} editando={editando} onChange={(valor) => setCampos({ ...campos, barrio: valor })} />
          <CampoFicha etiqueta="Ciudad" valor={campos.ciudad} editando={editando} onChange={(valor) => setCampos({ ...campos, ciudad: valor })} />
          <CampoFicha etiqueta="Departamento" valor={campos.departamento} editando={editando} onChange={(valor) => setCampos({ ...campos, departamento: valor })} />
        </div>

        <h4 className="mb-3 mt-6 border-t border-slate-100 pt-5 text-sm font-semibold text-slate-700">Cobertura</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoSelect etiqueta="Seguro médico" valor={campos.seguroMedico} editando={editando} opciones={['IPS', 'Asismed', 'Migone', 'OAMI', 'Medilife', 'Otro']} placeholder="Particular / sin seguro" onChange={(valor) => setCampos({ ...campos, seguroMedico: valor })} />
          {campos.seguroMedico && <>
            <CampoFicha etiqueta="N.º de contrato" valor={campos.numeroContrato} editando={editando} onChange={(valor) => setCampos({ ...campos, numeroContrato: valor })} />
            <CampoFicha etiqueta="N.º de afiliado" valor={campos.numeroAfiliado} editando={editando} onChange={(valor) => setCampos({ ...campos, numeroAfiliado: valor })} />
          </>}
        </div>

        <h4 className="mb-3 mt-6 border-t border-slate-100 pt-5 text-sm font-semibold text-slate-700">Otros datos</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoFicha etiqueta="Cómo llegó a la clínica (opcional)" valor={campos.comoLlego} editando={editando} onChange={(valor) => setCampos({ ...campos, comoLlego: valor })} />
          {edad && esMenorDeEdad(fechaNacimiento, new Date()) && <CampoFicha etiqueta="Colegio (opcional)" valor={campos.colegio} editando={editando} onChange={(valor) => setCampos({ ...campos, colegio: valor })} />}
        </div>

        {editando && (
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={guardando || faltantes.length > 0}
            className="mt-2 min-h-10 rounded-lg bg-teal-700 px-4 text-sm font-medium text-white disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar ficha'}
          </button>
        )}
      </div>
    </section>
  )
}

function CampoFicha({ etiqueta, valor, editando, onChange }: {
  etiqueta: string; valor: string; editando: boolean; onChange: (valor: string) => void
}) {
  return <div>
    <label className="block text-xs uppercase tracking-wide text-slate-400">{etiqueta}</label>
    {editando
      ? <input value={valor} onChange={(e) => onChange(e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" />
      : <p className="text-sm text-slate-800">{valor || '—'}</p>}
  </div>
}

function CampoSelect({ etiqueta, valor, editando, opciones, placeholder, onChange }: {
  etiqueta: string; valor: string; editando: boolean; opciones: string[]
  placeholder?: string; onChange: (valor: string) => void
}) {
  return <div>
    <label className="block text-xs uppercase tracking-wide text-slate-400">{etiqueta}</label>
    {editando ? <select value={valor} onChange={(e) => onChange(e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm">
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {opciones.map((opcion) => <option key={opcion} value={opcion}>{opcion}</option>)}
    </select> : <p className="text-sm text-slate-800">{valor || placeholder || '—'}</p>}
  </div>
}

/**
 * La pastilla de color del estado de una cita. La heredó la ficha y desde T23
 * la comparte con la solapa `Citas`: el mismo estado, el mismo color, un solo
 * lugar donde se pintan.
 */
export function EstadoPill({ estado }: { estado: string }) {
  const colores: Record<string, string> = {
    atendida: 'bg-teal-100 text-teal-900',
    facturada: 'bg-teal-100 text-teal-900',
    no_asistio: 'bg-rose-100 text-rose-900',
    cancelada: 'bg-slate-100 text-slate-600',
    pendiente: 'bg-amber-100 text-amber-900',
    confirmada: 'bg-sky-100 text-sky-900',
  }
  const etiquetas: Record<string, string> = {
    atendida: 'atendida',
    facturada: 'facturada',
    no_asistio: 'no asistió',
    cancelada: 'cancelada',
    pendiente: 'pendiente',
    confirmada: 'confirmada',
    en_atencion: 'en atención',
  }
  return (
    <span className={`rounded-full px-2 text-xs ${colores[estado] ?? 'bg-slate-100 text-slate-600'}`}>
      {etiquetas[estado] ?? estado}
    </span>
  )
}
